// Pure helpers over a GET /apps response — no fetch, no React. Kept separate
// from releaseApi.ts so the drift/empty-state logic is trivially unit-testable.
import type { AppSummary, AppRingStatus, BuildRecord } from '@platform/shared-types';

export interface TargetSummaryRow {
  target: string;
  newestBuild?: BuildRecord;
  rings: AppRingStatus[];
  /**
   * Rings whose CURRENT version differs from the newest registered build for
   * this target. A ring with no newest build at all is never "drifting" —
   * there is nothing newer to drift from, it's simply unbuilt (see
   * `hasAnyBuilds` below, the "no builds yet" state).
   */
  driftingRings: AppRingStatus[];
}

export interface EnvSummaryRow {
  env: AppSummary['environments'][number]['env'];
  targets: TargetSummaryRow[];
  driftCount: number;
}

export interface AppSummaryRow {
  appId: string;
  environments: EnvSummaryRow[];
  /** True once ANY target in ANY env has a newest build. */
  hasAnyBuilds: boolean;
  totalDrift: number;
}

/**
 * Summarise `GET /apps` for the Apps tab: per app/env/target, which rings
 * are pointing at something other than the newest registered build ("drift"),
 * and whether the app has any builds at all ("no builds yet" — the real,
 * expected state of a brand-new hub before CI has published anything).
 *
 * Never throws on an empty or partial response: `apps: []` summarises to
 * `[]` (the caller renders the empty state); an app with builds in dev but
 * none in prod summarises each env independently — a target with no
 * `newestBuild` simply has `driftingRings: []` and contributes to
 * `hasAnyBuilds` staying false for that target, not for the whole app.
 */
export function summariseApps(apps: AppSummary[]): AppSummaryRow[] {
  return apps.map((app) => {
    let hasAnyBuilds = false;
    let totalDrift = 0;

    const environments: EnvSummaryRow[] = app.environments.map((envEntry) => {
      const targets: TargetSummaryRow[] = envEntry.targets.map((t) => {
        if (t.newestBuild) hasAnyBuilds = true;
        const driftingRings = t.newestBuild
          ? t.rings.filter((r) => r.version !== t.newestBuild!.version)
          : [];
        return { target: t.target, newestBuild: t.newestBuild, rings: t.rings, driftingRings };
      });
      const driftCount = targets.reduce((sum, t) => sum + t.driftingRings.length, 0);
      totalDrift += driftCount;
      return { env: envEntry.env, targets, driftCount };
    });

    return { appId: app.appId, environments, hasAnyBuilds, totalDrift };
  });
}
